import { useCallback, useContext, useState } from 'react';

import { ApiPairs } from 'api';
import { DepositWithdrawContext } from 'context/DepositWithdrawContext/DepositWithdrawContext';
import { useTypedDispatch } from 'store';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { bn } from 'tools/math';
import { IDexPair } from 'types/pairs';
import { ITransfer } from 'types/transfers';

interface IUseWithdrawProps {
  onClose: () => void;
  onRemountTable: () => void;
  pair: IDexPair;
  onWithdrawResult?: (transfers: ITransfer[]) => void;
  receiver: string | undefined;
}

const useWithdraw = ({
  onClose,
  onRemountTable,
  pair,
  onWithdrawResult,
  receiver,
}: IUseWithdrawProps) => {
  const dispatch = useTypedDispatch();

  const [loading, setLoading] = useState(false);

  const { innerWalletsValid, formError, selectedToken, innerWallets } =
    useContext(DepositWithdrawContext);

  const handleWithdraw = useCallback(async () => {
    if (!receiver) {
      return onClose();
    }

    if (formError) return;

    if (!innerWalletsValid) return;

    try {
      setLoading(true);

      const { isSuccess, errorMessage, data } = await ApiPairs.withdraw({
        recipient_address: receiver,
        pairId: pair.id,
        transfers: innerWallets.get
          .filter(el => !el.disabled)
          .map(wallet => ({
            amount: wallet.max_amount
              ? undefined
              : bn(wallet.balance, selectedToken.get.decimals).toString(),
            max_amount: wallet.max_amount,
            token_id: selectedToken.get.id,
            wallet_id: wallet.id,
          })),
      });

      if (!isSuccess && errorMessage) {
        dispatch(
          setAlertState({
            type: 'failed',
            text: errorMessage,
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => dispatch(dropAlertState()),
          }),
        );
      } else if (isSuccess && data) {
        if (onWithdrawResult) {
          onWithdrawResult(data.transfers);
        }
        onRemountTable();
      }
    } catch (error) {
      console.log(error);

      dispatch(
        setAlertState({
          type: 'failed',
          text: 'Something went wrong...',
          onClose: () => dispatch(dropAlertState()),
          onSubmit: () => dispatch(dropAlertState()),
        }),
      );
    } finally {
      setLoading(false);
      onClose();
    }
  }, [
    innerWalletsValid,
    onClose,
    pair,
    formError,
    onRemountTable,
    selectedToken.get,
    innerWallets.get,
    dispatch,
    onWithdrawResult,
    receiver,
  ]);

  return {
    withdraw: handleWithdraw,
    loading,
  };
};

export default useWithdraw;
